import React, { useState, useEffect, useRef } from 'react';
import '../styles/modes/compareMode.css';

// Import sub-components
import ModeHelpTooltip from './ModeHelpTooltip';
import PathInfo from './PathInfo';
import ComparisonResult from './ComparisonResult';

// Import utility functions
import { 
  calculatePathDetails, 
  getAutoVehiclePaths, 
  getCustomVehiclePaths 
} from './utils';

// Create a throttled logging function for cross-component use
let lastCompareLogTime = 0;
const LOG_THROTTLE_MS = 2000; // Log at most once every 2 seconds

const throttledCompareLog = (message, data) => {
  const now = Date.now();
  if (now - lastCompareLogTime > LOG_THROTTLE_MS) {
    console.log(message, data);
    lastCompareLogTime = now;
  }
};

export default function CompareMode({ locations, startId, autoPath, customPath, vehicleCount = 2 }) {
  const [autoPathInfo, setAutoPathInfo] = useState(null);
  const [customPathInfo, setCustomPathInfo] = useState(null);
  const [pathDifference, setPathDifference] = useState(null);
  const [autoSegments, setAutoSegments] = useState([]);
  const [customSegments, setCustomSegments] = useState([]);
  const [expandedDetails, setExpandedDetails] = useState(false);
  // Help tooltip state
  const [showHelp, setShowHelp] = useState(false);
  
  // Use refs to track previous paths and avoid processing the same data repeatedly
  const prevAutoPathRef = useRef(null);
  const prevCustomPathRef = useRef(null);

  // Toggle help visibility
  const toggleHelp = () => setShowHelp(!showHelp);
  

  
  // Extract vehicle path data - unified reading approach
  const autoVehiclePaths = getAutoVehiclePaths(autoPath);
  const customVehiclePaths = getCustomVehiclePaths(customPath);
  

  
  // Add memoization to avoid unnecessary recalculations
  const memoizedAutoVehiclePaths = React.useMemo(() => autoVehiclePaths, 
    [autoPath ? JSON.stringify(autoPath) : null]);
  
  const memoizedCustomVehiclePaths = React.useMemo(() => customVehiclePaths, 
    [customPath ? JSON.stringify(customPath) : null]);
  
  // Add detailed logging to track vehicle path data
  useEffect(() => {
    console.log('[DEBUG-COMPARE-PATHS] Processed vehicle path data:', {
      hasAutoVehiclePaths: Boolean(memoizedAutoVehiclePaths),
      autoVehiclePathCount: memoizedAutoVehiclePaths?.length || 0, 
      autoVehiclePathsDetails: memoizedAutoVehiclePaths ? 
        memoizedAutoVehiclePaths.map(vp => ({id: vp.id, distance: vp.distance, pathLength: vp.path?.length || 0})) : 'null',
      hasCustomVehiclePaths: Boolean(memoizedCustomVehiclePaths),
      customVehiclePathCount: memoizedCustomVehiclePaths?.length || 0,
      customVehiclePathsDetails: memoizedCustomVehiclePaths ? 
        memoizedCustomVehiclePaths.map(vp => ({id: vp.id, distance: vp.distance, pathLength: vp.path?.length || 0})) : 'null',
      currentVehicleCount: vehicleCount
    });
  }, [memoizedAutoVehiclePaths, memoizedCustomVehiclePaths, vehicleCount]);
  
  // Calculate path information
  useEffect(() => {
    // Check if paths have changed; skip processing if unchanged
    const autoPathStr = JSON.stringify(autoPath);
    const customPathStr = JSON.stringify(customPath);
    
    if (autoPathStr === prevAutoPathRef.current && 
        customPathStr === prevCustomPathRef.current) {
      return; // Data hasn't changed, skip update
    }
    
    // Update reference values
    prevAutoPathRef.current = autoPathStr;
    prevCustomPathRef.current = customPathStr;
    

    
    // Calculate auto path information
    if (autoPath && (Array.isArray(autoPath) && autoPath.length > 1)) {
      const { distance, segments } = calculatePathDetails(autoPath, locations);
      
      // Directly use path array length, including all points (including duplicates)
      let autoPointCount = autoPath.length;
      
      // Set auto path information, distinguish between single and multi-vehicle modes
      setAutoPathInfo({
        distance: distance,
        pointCount: autoPointCount,
        complete: autoPath.length > 0 && autoPath[0] === autoPath[autoPath.length - 1],
        vehicleCount: memoizedAutoVehiclePaths ? memoizedAutoVehiclePaths.length : vehicleCount,
        vehiclePaths: memoizedAutoVehiclePaths
      });
      setAutoSegments(segments);
    } else {
      setAutoPathInfo(null);
      setAutoSegments([]);
    }

    // Calculate custom path information
    if (customPath && (Array.isArray(customPath) && customPath.length > 1)) {
      const { distance, segments } = calculatePathDetails(customPath, locations);
      
      // Directly use path array length, including all points (including duplicates)
      let customPointCount = customPath.length;
      
      // Set custom path information, distinguish between single and multi-vehicle modes
      // Use the passed vehicleCount instead of hardcoded value
      const customVehicleCount = memoizedCustomVehiclePaths ? memoizedCustomVehiclePaths.length : 
        (Array.isArray(customPath) && customPath.length > 2 ? vehicleCount : 1);
      
      setCustomPathInfo({
        distance: distance,
        pointCount: customPointCount,
        complete: customPath.length > 0 && customPath[0] === customPath[customPath.length - 1],
        vehicleCount: customVehicleCount,
        vehiclePaths: memoizedCustomVehiclePaths
      });
      setCustomSegments(segments);
    } else {
      setCustomPathInfo(null);
      setCustomSegments([]);
    }
  // Use memoized dependencies to reduce unnecessary re-renders
  }, [
    autoPath, 
    customPath, 
    locations, 
    vehicleCount, 
    memoizedAutoVehiclePaths, 
    memoizedCustomVehiclePaths
  ]);

  // Calculate path difference
  useEffect(() => {
    // Only calculate difference if both paths have valid data
    if (autoPathInfo && customPathInfo && 
        typeof autoPathInfo.distance === 'number' && 
        typeof customPathInfo.distance === 'number' &&
        autoPathInfo.distance > 0) {
      
      const distanceDiff = customPathInfo.distance - autoPathInfo.distance;
      const percentDiff = (distanceDiff / autoPathInfo.distance) * 100;
      
      // Debug: Log distance values for debugging (can be removed in production)
      // console.log('[COMPARE-MODE] Distance comparison:', {
      //   autoDistance: autoPathInfo.distance,
      //   customDistance: customPathInfo.distance,
      //   distanceDiff: distanceDiff,
      //   percentDiff: percentDiff,
      //   autoPath: autoPath,
      //   customPath: customPath
      // });
      
      // Consider paths equal if difference is very small (< 0.01 km = 10 meters)
      const TOLERANCE = 0.01;
      const isEffectivelyEqual = Math.abs(distanceDiff) < TOLERANCE;
      
      setPathDifference({
        absoluteDiff: Math.abs(distanceDiff),
        percentDiff: percentDiff,
        isCustomBetter: isEffectivelyEqual ? null : distanceDiff < 0, // null means equal
        isEffectivelyEqual: isEffectivelyEqual
      });
    } else {
      setPathDifference(null);
    }
  }, [autoPathInfo, customPathInfo]);
  
  // Toggle detailed comparison display
  const toggleDetails = () => {
    setExpandedDetails(!expandedDetails);
  };

  return (
    <div className="compare-mode-container">
      <div className="mode-description">
        <div className="mode-title-container">
          <h3>Compare Mode</h3>
          <button 
            className="mode-help-button"
            onClick={toggleHelp}
            title="Help for Compare Mode"
          >
            ?
          </button>
        </div>
        <p>Compare performance differences between auto and custom paths.</p>
      </div>
      
      {/* Help tooltip */}
      {showHelp && <ModeHelpTooltip onClose={() => setShowHelp(false)} />}
      
      <div className="comparison-panel">
        <h4>Path Comparison</h4>
        
        {(!autoPath || !customPath) && (
          <div className="empty-paths-message">
            <p>Please create paths in "Auto Find Path" and "Custom Path" modes first, then compare.</p>
          </div>
        )}
        
        {(autoPath && customPath) && (
          <div className="paths-comparison">
            <div className="path-info-container">
              {/* Auto Path Information */}
              <PathInfo 
                pathInfo={autoPathInfo} 
                path={autoPath} 
                locations={locations} 
                title="Auto Path (Algorithm Solution)" 
                className="auto" 
                isEmpty="auto"
              />
              
              {/* Custom Path Information */}
              <PathInfo 
                pathInfo={customPathInfo} 
                path={customPath} 
                locations={locations} 
                title="Custom Path (Manual Planning)" 
                className="custom" 
                isEmpty="custom"
              />
            </div>
            
            {/* Comparison Results */}
            <ComparisonResult 
              pathDifference={pathDifference}
              autoPathInfo={autoPathInfo}
              customPathInfo={customPathInfo}
              expandedDetails={expandedDetails}
              toggleDetails={toggleDetails}
              autoSegments={autoSegments}
              customSegments={customSegments}
            />
          </div>
        )}
      </div>
    </div>
  );
} 